import { useMemo, useState } from 'react'

import { AgChartOptions, AgLineSeriesOptions } from 'ag-charts-community'
import { parseISO } from 'date-fns'

import { groupByKey } from '@fortem-monorepo/js-utilities'
import { CustomAgChart } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { DataTableFacetedFilter } from '@fortem-monorepo/react-ui'

import { sharedOptions, timeAxis } from '@/pages/flow/rfq/components/market-data-options'
import { useRFQRateQuery, useRFQVolQuery } from '@/react-query/queries/rfq'

export interface MarketDataTableProps {
  options?: Omit<AgChartOptions, 'series' | 'axes'>
}

export const MarketDataTable: React.FC<MarketDataTableProps> = ({ options = {} }) => {
  const [selectedVols, setSelectedVols] = useState<string[]>([])
  const [selectedRates, setSelectedRates] = useState<string[]>([])

  const vols = useRFQVolQuery()
  const rates = useRFQRateQuery()

  const volData = useMemo(() => {
    return (vols.data ?? [])
      .filter((item) => {
        return !isNaN(new Date(item.asatdate).getTime())
      })
      .map((item) => ({
        ...item,
        date: parseISO(item.asatdate),
      }))
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .filter((item) => item.date.getFullYear() > 2015)
  }, [vols.data])

  const rateData = useMemo(() => {
    return (rates.data ?? [])
      .filter((item) => {
        return !isNaN(new Date(item.asatdate).getTime())
      })
      .map((item) => ({
        ...item,
        date: parseISO(item.asatdate),
      }))
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .filter((item) => item.date.getFullYear() > 2015)
  }, [vols.data])

  const uniqueVols = [...new Set(volData.map((item) => item.ticker))]
  const uniqueCcys = [...new Set(rateData.map((item) => item.ccy))]

  const volGrouped = useMemo(() => {
    return groupByKey(volData, 'ticker')
  }, [volData])

  const interestRateGrouped = useMemo(() => {
    return groupByKey(rateData, 'ccy')
  }, [rateData])

  return (
    <Widget>
      <WidgetHeaderWithQuery
        query={{
          refetch: async () => {
            return await Promise.all([vols.refetch(), rates.refetch()])
          },
          isFetching: [vols, rates].some((quer) => quer.isFetching),
          isLoading: [vols, rates].some((quer) => quer.isLoading),
          dataUpdatedAt: [vols, rates]
            .map((quer) => quer.dataUpdatedAt)
            .sort()
            .sort((a, b) => b - a)[0],
        }}
        title="Market Data"
      >
        <div className="items-center flex gap-2 px-3 py-2 border-t">
          <DataTableFacetedFilter
            size={'sm'}
            title={'Vol Index'}
            values={selectedVols}
            setValues={setSelectedVols}
            options={uniqueVols.map((ticker) => ({ label: ticker, value: ticker }))}
          />
          <DataTableFacetedFilter
            size={'sm'}
            title={'Rate CCY'}
            values={selectedRates}
            setValues={setSelectedRates}
            options={uniqueCcys.map((ccy) => ({ label: ccy, value: ccy }))}
          />
        </div>
      </WidgetHeaderWithQuery>
      <WidgetContent>
        <CustomAgChart
          options={{
            ...sharedOptions,
            series: [
              // Vol levels
              ...Object.entries(volGrouped)
                .filter(([ticker]) => selectedVols.includes(ticker))
                .map(
                  ([ticker, _data]) =>
                    ({
                      type: 'line',
                      interpolation: { type: 'smooth' },
                      data: _data,
                      xKey: 'date',
                      yKey: 'vol',
                      yName: `${ticker} vol`,
                      marker: {
                        size: 0,
                      },
                    }) satisfies AgLineSeriesOptions,
                ),
              // Interest rates
              ...Object.entries(interestRateGrouped)
                .filter(([ccy]) => selectedRates.includes(ccy))
                .map(
                  ([ccy, _data]) =>
                    ({
                      type: 'line',
                      interpolation: { type: 'smooth' },
                      data: _data,
                      xKey: 'date',
                      yKey: 'rate',
                      yName: `${ccy} rate`,
                      marker: {
                        size: 0,
                      },
                    }) satisfies AgLineSeriesOptions,
                ),
            ],
            axes: [
              timeAxis,
              {
                type: 'number',
                position: 'left',
                crosshair: {
                  enabled: true,
                },
                // min: -0.01,
                nice: false,
                // max: 0.4,
                keys: ['vol'],
              },
              {
                type: 'number',
                position: 'right',
                crosshair: {
                  enabled: true,
                },
                // min: -0.01,
                nice: false,
                // max: 0.4,
                keys: ['rate'],
              },
            ],
            ...options,
          }}
        />
      </WidgetContent>
    </Widget>
  )
}
